import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  CheckoutStatusDto,
  PlanDto,
  PlanType,
  UserSubscriptionDto
} from '../models/subscriptions';

/** Backend may serialize enums as numbers: Writer=2, Trial=1, Active=2 */
function isWriterPlan(planType: PlanType | number): boolean {
  return planType === 'Writer' || planType === 2;
}
function isActiveOrTrialStatus(status: string | number): boolean {
  return status === 'Active' || status === 'Trial' || status === 1 || status === 2;
}

/** Blocks starting another subscription of same plan type (matches backend rules). */
function isBlockingSubscriptionStatus(status: string | number): boolean {
  return isActiveOrTrialStatus(status);
}

export interface PaymentHistoryDto {
  id: number;
  amount: number;
  currencyCode: string;
  paymentStatus: string;
  paymentMethod?: string;
  paidAtUtc?: string;
  originalAmount?: number;
  discountAmount?: number;
  voucherCodeSnapshot?: string;
  cycleIndex: number;
}

export interface CheckoutResolution {
  hasActiveSubscription: boolean;
  paymentCompleted: boolean;
  paymentFailed: boolean;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionsService {
  constructor(private api: ApiService) {}

  getPlans(type?: PlanType): Observable<PlanDto[]> {
    const params = type ? { type } : undefined;
    return this.api.getData<PlanDto[]>('subscriptions/plans', params as Record<string, unknown>);
  }

  getPlan(planId: number): Observable<PlanDto> {
    return this.api.getData<PlanDto>(`subscriptions/plans/${planId}`);
  }

  /** Silent-fail-friendly: callers typically use catchError(of([])); suppress the toast. */
  getMySubscriptions(): Observable<UserSubscriptionDto[]> {
    return this.api.getData<UserSubscriptionDto[]>('subscriptions/my', undefined, { suppressToast: true });
  }

  hasActiveWriterSubscription(): Observable<boolean> {
    return this.getMySubscriptions().pipe(
      map(subs =>
        subs.some(s => isWriterPlan(s.planType) && isBlockingSubscriptionStatus(s.status))
      )
    );
  }

  /** Start a subscription. Errors are surfaced inline by checkout, so suppress the toast. */
  subscribe(planId: number): Observable<CheckoutStatusDto> {
    return this.api.postData<CheckoutStatusDto>('subscriptions/subscribe', { planId }, { suppressToast: true });
  }

  cancel(subscriptionId: number, reason?: string): Observable<void> {
    return this.api.postData<void>('subscriptions/cancel', { subscriptionId, reason });
  }

  reactivate(): Observable<void> {
    return this.api.postData<void>('subscriptions/reactivate', {});
  }

  resumeCheckout(subscriptionId: number): Observable<CheckoutStatusDto> {
    return this.api.postData<CheckoutStatusDto>(
      `subscriptions/${subscriptionId}/resume-checkout`,
      {}
    );
  }

  getPaymentHistory(): Observable<PaymentHistoryDto[]> {
    return this.api.getData<PaymentHistoryDto[]>('subscriptions/payment-history');
  }

  verifyPayment(paymentId: number): Observable<void> {
    return this.api.postData<void>(`subscriptions/verify-payment/${paymentId}`, {});
  }

  resolveCheckout(paymentId?: number): Observable<CheckoutResolution> {
    return forkJoin({
      subs: this.getMySubscriptions(),
      payments: this.getPaymentHistory()
    }).pipe(
      map(({ subs, payments }) => {
        const hasActiveSubscription = subs.some(s => isActiveOrTrialStatus(s.status));
        const payment = paymentId ? payments.find(p => p.id === paymentId) : undefined;
        const raw = payment?.paymentStatus;
        const psNum = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
        const psLower = String(raw ?? '').toLowerCase();
        const paymentCompleted =
          (!Number.isNaN(psNum) && (psNum === 1 || psNum === 4)) ||
          psLower === 'completed' ||
          psLower === 'paid' ||
          psLower === 'voided';
        const paymentFailed =
          (!Number.isNaN(psNum) && psNum === 2) || psLower === 'failed';
        return { hasActiveSubscription, paymentCompleted, paymentFailed };
      })
    );
  }
}
